import React, { ReactNode, useState } from 'react'
import { 
  Layout, 
  Menu, 
  Avatar, 
  Dropdown, 
  Button, 
  Typography,
  Space,
  theme
} from 'antd'
import {
  ShoppingOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  ShopOutlined,
  MessageOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CustomerServiceOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSiteSettings } from '../../contexts/SiteSettingsContext'

const { Header, Sider, Content } = Layout
const { Text } = Typography

interface AdminLayoutProps {
  children: ReactNode
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const { user, logout } = useAuth()
  const { settings, getLocalizedText } = useSiteSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken()

  // Menu items for admin navigation
  const menuItems = [
    {
      key: '/admin/products',
      icon: <ShoppingOutlined />,
      label: 'Products',
    },
    {
      key: '/admin/categories',
      icon: <AppstoreOutlined />,
      label: 'Categories',
    },
    {
      key: '/admin/articles',
      icon: <FileTextOutlined />,
      label: 'Articles',
    },
    {
      key: '/admin/stores',
      icon: <ShopOutlined />,
      label: 'Stores',
    },
    {
      key: '/admin/site-settings',
      icon: <MessageOutlined />,
      label: 'Site Content',
    },
    {
      key: '/admin/service-content',
      icon: <CustomerServiceOutlined />,
      label: 'Service Content',
    },
    {
      key: '/admin/about-content',
      icon: <InfoCircleOutlined />,
      label: 'About Us Content',
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  const handleLogout = () => {
    logout()
    navigate('/admin/login')
  }

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile Settings',
      onClick: () => navigate('/admin/profile'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ]

  return (
    <Layout className="min-h-screen">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="flex items-center justify-center h-16 border-b border-gray-700 px-2">
          {settings?.logo?.url ? (
            <div className="flex items-center justify-center">
              <img 
                src={settings.logo.url} 
                alt={getLocalizedText(settings.logo.alt) || 'Logo'} 
                style={{
                  maxHeight: collapsed ? '32px' : '40px',
                  maxWidth: collapsed ? '32px' : '150px',
                  height: 'auto',
                  width: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <span class="text-white font-bold text-lg">
                        ${collapsed ? 'MC' : 'MedCareHub'}
                      </span>
                    `;
                  }
                }}
              />
            </div>
          ) : (
            <Text className="text-white font-bold text-lg">
              {collapsed ? 'MC' : 'MedCareHub'}
            </Text>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'all 0.2s' }}>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: colorBgContainer,
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          
          <Space>
            <Text>Welcome, {user?.name}</Text>
            <Dropdown 
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Button type="text" className="flex items-center">
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />} 
                  style={{ backgroundColor: '#1890ff' }}
                />
              </Button>
            </Dropdown>
          </Space>
        </Header>
        
        <Content
          style={{
            margin: '24px',
            padding: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            overflow: 'auto'
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminLayout
